import PropTypes from 'prop-types';
import React from 'react';

import Category from '../../models/category';
import Contingent from '../../models/contingentModel';
import Inventory from '../../models/inventory';
import EventModel from '../../models/eventModel';
import RateTemplate from '../../models/rateTemplate';
import Form from '../../models/form';
import Loading from '../../components/loading';
import ConfirmModal from '../../components/confirmModal';
import { RateStatus } from './components/rateStatus';
import RateMaxPrice from '../../models/rateMaxPrice';
import QuotasModel from '../../models/quotasModel';

import { Icon, Popup } from 'semantic-ui-react';
import { Tooltip, WeezHeader, Button, Card, Message, OptionalForm, ToastContext, TooltipHelp, ToggleCard } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import { useSegment, useTrackingPage } from '../../components/tracking/trackingProvider';
import useMixpanel from '../../context/useMixpanel';
import Chart from '../../models/chart';
import { Link } from 'react-router-dom';
import { useForm, requiredField } from '../../utils/validators';
import useAppContext from '../../context/useAppContext';
import RateFormInfos from './rateFormInfos';
import RateFormSeating from './rateFormSeating';
import RateFormSettingsEvents from './rateFormSettingsEvents';
import { undefined_or_null_or_value } from '../../utils/tools';

import PassCultureLogo from '../ticket_option/integration/images/passculture.png';

import Styles from '../../styles/rate/rateCreation.module.css';
import ReleaseCard from './createOrUpdate/releaseCard';
import CartCard from './createOrUpdate/cartCard';
import EventSettings from '../../models/eventSettings';
import moment from 'moment/moment';
import { LANGUAGES } from '../../components/translations/translationModal';
import PassCultureEvent from '../../models/passCultureEvent';
import PageError from '../error/pageError';
import TaxModel from '../../models/taxModel';
import TicketManagement from './components/ticketManagement';
import { usePermissions } from '../../user/permissions';
import { RateMembershipInfo } from './components/rateMembershipInfo';

export function TemplatePopup({ template, toRatePage = true }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();

    return (
        <Link target="_blank" to={`/ticket/O${organization.id}/rates/${toRatePage ? template['uuid'] : ''}`}>
            <Popup content={<span>{t('ticket.rates.editTemplate')}</span>} trigger={<Icon name="linkify" />} />
        </Link>
    );
}

TemplatePopup.propTypes = {
    template: PropTypes.object.isRequired,
    toRatePage: PropTypes.bool.isRequired
};

export function InheritedInput({ children, rate, property, onChange = () => {}, formState, linkPosition = 'right' }) {
    const isChecked = React.useMemo(() => {
        return Boolean(formState?.[property]);
    }, [formState, property]);

    const isDisabled = React.useMemo(() => {
        if ((formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price') {
            return true;
        }
        return rate?.rate_template ? !isChecked : false;
    }, [formState, property, rate, isChecked]);

    const inputValue = React.useMemo(() => {
        if ((formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price') {
            // @Todo repair here on change price value form if typ$e === free//
            if ((rate?.rate_template && !isChecked ? rate?.rate_template[property] : formState?.[property]) !== 0) {
                // onChange(0);
            }
            return 0;
        }
        let value = rate?.rate_template && !isChecked ? rate?.rate_template[property] : formState?.[property];
        return isNaN(Number(value)) ? value : Number(value);
    }, [formState, property, rate, isChecked]);

    const hideIconLinker = React.useMemo(() => {
        return (formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price';
    }, [formState, property, rate]);

    React.Children.only(children);
    let cloneChildren = React.cloneElement(children, {
        ...children.props,
        disabled: isDisabled,
        value: inputValue
    });

    const { t } = useTranslation();

    if (!rate?.rate_template || hideIconLinker) {
        return cloneChildren;
    }

    return (
        <div className="inherited-label-value-container">
            {linkPosition === 'right' && cloneChildren}
            <Tooltip triggerOnHover title={<span>{isChecked ? t('ticket.rate_template.link_field_template') : t('ticket.rate_template.unlink_field_template')}</span>}>
                <Icon
                    className="inherit-label-icon"
                    name={isChecked ? 'unlink' : 'linkify'}
                    onClick={() => {
                        if (isChecked) {
                            onChange(null);
                        } else {
                            onChange(rate.rate_template[property]);
                        }
                    }}
                />
            </Tooltip>
            {linkPosition === 'left' && cloneChildren}
        </div>
    );
}

InheritedInput.propTypes = {
    rate: PropTypes.object.isRequired,
    property: PropTypes.string.isRequired,
    children: PropTypes.node,
    onChange: PropTypes.func,
    formState: PropTypes.object
};

export default function RateForm({ organizationId, event, rateId, history, RateClass, GroupClass }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const mixpanel = useMixpanel();
    const [{ organization }] = useAppContext();
    const [rates, setRates] = React.useState([]);
    const [rateTemplates, setRateTemplates] = React.useState(null);
    const permissions = usePermissions();

    const toast = React.useContext(ToastContext);

    const [charts, setCharts] = React.useState([]);
    const [inventories, setInventories] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [events, setEvents] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [rate, setRate] = React.useState(null);
    const [contingents, setContingents] = React.useState([]);
    const [forms, setForms] = React.useState([]);
    const [settings, setSettings] = React.useState(null);
    const [isLoading, setLoading] = React.useState(true);
    const [formerGroupId, setGroupId] = React.useState(null);
    const [selectedEvents, setSelectedEvents] = React.useState([]);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [hasRateTemplateId, setHasRateTemplateId] = React.useState(false);
    const [salesStart, setSalesStart] = React.useState(null);
    const [openOptionalForm, setOpenOptionalForm] = React.useState(false);
    const [isPassCultureEventActivated, setIsPassCultureEventActivated] = React.useState(false);
    const [pageErrorCode, setPageErrorCode] = React.useState(null);

    let validators = rate?.rate_template ? {} : { name: requiredField };

    useTrackingPage(`VIEW - ${rateId ? 'EDIT' : 'CREATE'} RATE`, { 'Page Title': 'RATE' });

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event, rate]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return event ? defaultLocale : defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions, event]);

    const [taxes, ,] = TaxModel.useApiModel({});

    RateMaxPrice.useApiModel(
        {
            eventId: event ? event.pk : null,
            organizationId: organizationId,
            launch: organizationId && event !== undefined,
            onSuccess: result => {
                onChangeForm('maxPrice', result.max_price);
            }
        },
        [event, organizationId]
    );

    const validateDate = React.useCallback(
        (name, date, form) => {
            let tmpStart = form.visibilityStartDate ? moment.tz(form.visibilityStartDate, event.timezone) : null;
            let tmpEnd = form.visibilityEndDate ? moment.tz(form.visibilityEndDate, event.timezone) : null;
            if (tmpStart && tmpEnd && tmpStart.isAfter(tmpEnd)) {
                return 'ticket.rates.optionalSettings.release.startDateBeforeEndDate';
            }
            return true;
        },
        [event]
    );

    const validateLimitsQuantity = React.useCallback(
        (name, quantity, form) => {
            // validation of min and max quantity in a cart
            let tmpMax = form.maxQuantity;
            let tmpMin = form.minQuantity;
            // TODO add validator no null
            if (tmpMax && tmpMin && tmpMin > tmpMax) {
                return t('ticket.rates.optionalSettings.release.minGreaterThanMax');
            }
            return true;
        },
        [event]
    );

    // In a rate template context, there is no event_id so this request must be skipped
    const [quotas, loadingQuotas] = QuotasModel.useApiModel(
        {
            organizationId: organizationId,
            eventId: event?.pk,
            getForRates: true,
            cache: false,
            allow_cache: false,
            query: {
                id_date: 0
            },
            launch: Boolean(event)
        },
        [organizationId, event]
    );

    const rateQuotaFromMenhir = React.useMemo(() => {
        return quotas ? quotas.find(quota => quota.rate_id === rate?.id && quota.event_id === event.id && quota.session_id === 0) : 0;
    }, [quotas, rate, event]);

    const validateQuantity = React.useCallback(
        (name, value) => {
            if (settings?.quota > 0 && value !== 0 && value > settings?.quota - event.attendees + rateQuotaFromMenhir?.quota_taken) {
                return t('ticket.rates.infos.quantityExceed');
            }
            return true;
        },
        [event, settings, rateQuotaFromMenhir]
    );

    // Inventory and Category are mandatory for seated events without sessions
    const validateOnlySeated = React.useCallback(
        (name, value, form) => {
            if (!event || form.rate_template_id) return true;
            if (((event.chart && event.is_seated) || (event?.isFakeSeating() && (inventories?.length > 1 || categories?.length > 1))) && !value?.length)
                return t('ticket.common.required');
            return true;
        },
        [event, inventories, categories]
    );

    let [formState, onChangeForm, onChangeFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            type: null,
            price: 0,
            quantity: null,
            tax_id: null,
            form_id: null,
            disabled: false,
            description: '',
            inventories: null,
            category: null,
            contingents: null,
            group: null,
            fees: [],
            maxPrice: 3000,
            maxQuantity: null,
            minQuantity: null,
            bundleSize: 1,
            defaultQuantity: null,
            visibilityStartDate: null,
            visibilityEndDate: null,
            relays: [],
            translationsName: {},
            translationsDescription: {},
            is_passculture: false
        },
        {
            ...validators,
            ...{
                quantity: event ? validateQuantity : () => true,
                visibilityStartDate: validateDate,
                visibilityEndDate: validateDate,
                maxQuantity: validateLimitsQuantity,
                minQuantity: validateLimitsQuantity,
                inventories: validateOnlySeated,
                category: validateOnlySeated
            }
        }
    );

    const RATE_TYPES = {
        fixed: {
            type: 'fixed',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNamePaid')
        },
        free: {
            type: 'free',
            price: 0,
            name: t('ticket.creationEvent.rates.priceNameFree')
        },
        open: {
            type: 'open',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNameDonation')
        }
    };

    const extractTranslationsKeys = React.useCallback(values => {
        return values.replace(/<[^>]+>|\r|\n/g, '').split('|');
    }, []);

    const extractDefaultTranslations = React.useCallback(
        (trads, value, lng) => {
            const language = lng.replace('-', '_');
            // For a rate juste created, description doesn't hold a pipe translate and default language key is
            // not set in translations
            if (!value.includes('|')) {
                return lng !== defaultLanguage ? trads[lng] : value;
            }

            // Extract values from pipe translate according to translations keys
            let key = extractTranslationsKeys(value).find(key => key.toLowerCase().includes(`[${language}]`));
            return key ? key.slice(7) : '';
        },
        [extractTranslationsKeys, defaultLanguage]
    );

    React.useEffect(() => {
        window.scrollTo({ top: 0 });
        setLoading(true);

        let ratePromise = null;
        let categoryPromise = null;
        let inventoryPromise = null;
        let contingentPromise = null;
        let chartPromise = null;
        let rateTemplatePromise = null;
        let formPromise = null;
        let settingsPromise = null;
        let rateGroupPromise = GroupClass.getAll({ organizationId, eventId: event ? event.id : null });
        let ratesPromise = RateClass.getAll({ organizationId, eventId: event ? event.id : null });
        if (rateId) {
            ratePromise = RateClass.get({ id: rateId, organizationId, eventId: event ? event.id : null }, { expand: 'relays' });
        }

        if (!event) {
            chartPromise = Chart.getAll({ organizationId, query: { only_seated: true } });

            let eventElements = EventModel.getAll({ organizationId, query: { include_deleted: true } });
            Promise.all([eventElements])
                .then(([eventElements]) => {
                    setEvents(eventElements.sort((el1, el2) => new Date(el2.start_date).getTime() - new Date(el1.start_date).getTime()));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(e);
                });
        }

        if (event) {
            rateTemplatePromise = RateTemplate.getAll({ organizationId });
            formPromise = Form.getAll({ organizationId, eventId: event.id });
            settingsPromise = EventSettings.get({ id: null, organizationId, eventId: event.id });
        }

        if (event?.isSeatedForRates()) {
            categoryPromise = Category.getAll({ organizationId, chartId: event.chart });
            inventoryPromise = Inventory.getAll({ organizationId, chartId: event.chart });
            contingentPromise = Contingent.getAll({ organizationId, eventId: event.id });
        }

        Promise.all([
            ratePromise,
            settingsPromise,
            categoryPromise,
            inventoryPromise,
            rateGroupPromise,
            contingentPromise,
            ratesPromise,
            chartPromise,
            rateTemplatePromise,
            formPromise
        ])
            .then(
                ([
                    rateElement,
                    settingsElement,
                    categoriesElements,
                    inventoriesElements,
                    rateGroups,
                    contingentsElements,
                    ratesElements,
                    charts,
                    rateTemplatesElements,
                    formElements
                ]) => {
                    let tmpInventories = [];
                    let tmpCategories = [];
                    if (event?.isSeatedForRates()) {
                        tmpInventories = inventoriesElements.filter(inventory => inventory.event === event.id);
                        tmpCategories = categoriesElements;
                        setInventories(tmpInventories);
                        setCategories(categoriesElements);
                        setContingents(contingentsElements);
                    }

                    // Case rate edition
                    if (rateId) {
                        setGroupId(rateElement.group);

                        let form = {
                            name: rateElement.name,
                            price: rateElement.price,
                            group: rateElement.group,
                            form_id: rateElement.form, //when pyvar will send form object , put form.id here
                            contingents: rateElement.contingents || [],
                            inventories: rateElement.inventories ? rateElement.inventories[0] : null,
                            category: rateElement.category,
                            type: rateElement.type,
                            quantity: rateElement.quantity,
                            tax_id: rateElement.tax_id,
                            disabled: rateElement.disabled,
                            description: rateElement.description,
                            fees: rateElement.fees || [],
                            maxQuantity: rateElement.cart_max_quantity,
                            minQuantity: rateElement.cart_min_quantity,
                            bundleSize: rateElement.bundle_size,
                            defaultQuantity: rateElement.preselected_quantity,
                            visibilityStartDate: rateElement.visibility_start_date,
                            visibilityEndDate: rateElement.visibility_end_date,
                            relays: rateElement.relays?.map(relay => relay.id),
                            translations: {},
                            translationsName: 'name' in rateElement.translations ? rateElement.translations.name : {},
                            translationsDescription: 'description' in rateElement.translations ? rateElement.translations.description : {},
                            is_passculture: rateElement.is_passculture
                        };

                        if (rateElement.category && tmpCategories.find(cat => cat.pk === rateElement.category)?.general_admission) {
                            form.contingents = [];
                        }

                        if (!rateElement.rate_template && (!Object.keys(form.translationsName).length || !(defaultLanguage in form.translationsName))) {
                            form.translationsName[defaultLanguage] = rateElement.name;
                        }

                        if (!rateElement.rate_template) {
                            const trans = Object.keys(form.translationsDescription);
                            if (trans.length) {
                                trans.forEach(key => {
                                    form.translationsDescription[key] = extractDefaultTranslations(form.translationsDescription, rateElement.description, key);
                                });
                                form.translationsDescription[defaultLanguage] = extractDefaultTranslations(form.translationsDescription, rateElement.description, defaultLanguage);
                            } else if (rateElement.description.includes('|')) {
                                // handle case there is a pipe in description (old menhir)
                                const keys = extractTranslationsKeys(rateElement.description);
                                keys.forEach(k => {
                                    const key = k
                                        .slice(1, 6)
                                        .toLowerCase()
                                        .replace('_', '-');
                                    form.translationsDescription[key] = extractDefaultTranslations(form.translationsDescription, rateElement.description, key);
                                });
                            } else {
                                // handle case there are no translations, and value is based on field itself
                                form.translationsDescription[defaultLanguage] = rateElement.description;
                            }
                        }

                        if (rateElement.rate_template) {
                            form.rate_template_id = rateElement.rate_template.pk;
                            setHasRateTemplateId(true);
                        }

                        //Put seats objects automatically if there is only one object
                        if (tmpInventories.length === 1 && !rateElement.rate_template && !rateElement.inventories?.length) {
                            form.inventories = tmpInventories[0].pk;
                        }
                        if (tmpCategories.length === 1 && !rateElement.rate_template && !rateElement.category) {
                            form.category = tmpCategories[0].pk;
                        }

                        onChangeFormObject(form);
                        setRate(rateElement);
                        setSalesStart(form.visibilityStartDate ? 'date' : form.relays?.length ? 'relays' : null);
                        setOpenOptionalForm(
                            Boolean(
                                form.relays?.length ||
                                    form.visibilityEndDate ||
                                    form.visibilityStartDate ||
                                    form.defaultQuantity ||
                                    form.bundleSize !== 1 ||
                                    form.minQuantity ||
                                    form.maxQuantity
                            )
                        );
                    } else {
                        // Case rate creation
                        let newRate = new RateClass();
                        let translationsDefaultValue = {};
                        if (!newRate.rate_template) {
                            translationsDefaultValue[defaultLanguage] = RATE_TYPES.fixed.name;
                        }
                        let form = { ...formState, ...RATE_TYPES.fixed, translationsName: translationsDefaultValue };

                        //Put seats objects automatically if there is only one object
                        if (tmpInventories.length === 1) {
                            form.inventories = tmpInventories[0].pk;
                        }
                        if (tmpCategories.length === 1) {
                            form.category = tmpCategories[0].pk;
                        }
                        onChangeFormObject(form);
                        setRate(newRate);
                    }

                    setRateTemplates(rateTemplatesElements);
                    setForms(formElements || []);

                    RateClass.sort(ratesElements);
                    setRates(ratesElements);

                    setGroups(rateGroups);
                    setSettings(settingsElement);

                    let categoriesPromises = [];
                    let contingentsPromises = [];

                    //Get all categories and all contigents for case rate_template
                    if (!event) {
                        charts.forEach(chart => {
                            let categoryPromise = Category.getAll({
                                organizationId,
                                chartId: chart.pk
                            });
                            categoriesPromises.push(categoryPromise);

                            let contingentPromise = Contingent.getAll({
                                organizationId: organizationId,
                                chartId: chart.pk
                            });
                            contingentsPromises.push(contingentPromise);
                        });
                    }

                    let allCategories = Promise.all(categoriesPromises);
                    let allContingents = Promise.all(contingentsPromises);

                    return Promise.all([charts, allCategories, allContingents]);
                }
            )
            .then(([charts, categories, contingents]) => {
                setCharts(charts);

                categories.forEach((cats, i) => {
                    cats.forEach(category => {
                        category.chart = charts[i].uuid;
                    });
                });
                if (!event) {
                    setContingents(contingents.flat(2));
                    setCategories(categories.flat(2));
                }
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.common.toast_error'));
                setPageErrorCode(e?.code || 500);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const [_] = PassCultureEvent.useApiModel(// eslint-disable-line
        {
            organizationId: organization.id,
            eventId: event?.id,
            launch: Boolean(event?.isFakeSeating()),
            onSuccess: result => {
                if (result?.response?.id) {
                    setIsPassCultureEventActivated(true);
                }
            }
        },
        [organization, event]
    );

    const prefixUrl = React.useCallback(() => {
        return event ? `/ticket/O${organizationId}/E${event.id}/settings/rates` : `/ticket/O${organizationId}/rates`;
    }, [organizationId, event]);

    const handleDisplayOrder = React.useCallback(
        rate => {
            //Set display_order of the current rate
            let lengthGroup = rates.filter(frate => frate.group === rate.group).length || 0;
            rate.display_order = lengthGroup;
            //Set display_order of the other rates from the former group
            let tmpGroup = rates.filter(frate => frate.group === formerGroupId && frate.pk !== rate.pk);
            //Set the new display order if different
            let editedRates = [];
            tmpGroup.forEach((frate, i) => {
                if (frate.display_order !== i) {
                    frate.display_order = i;
                    frate.translations = { name: {} }; //handle translations issue
                    editedRates.push(frate);
                }
            });

            if (!editedRates.length) {
                return rate.display_order;
            }

            RateClass.save(editedRates, {
                organizationId,
                eventId: event ? event.id : null
            }).catch(e => {
                toast.error(e);
            });

            return rate.display_order;
        },
        [rates, organizationId, event]
    );

    const hasNameTranslations = React.useMemo(() => {
        if (!event || !formState.translationsName || !Object.keys(formState.translationsName)) {
            return false;
        }
        return defaultLanguage in formState.translationsName && Object.keys(formState.translationsName).length > 1;
    }, [formState, defaultLanguage, event]);

    const hasDescriptionTranslations = React.useMemo(() => {
        if (!event || !formState.translationsDescription || !Object.keys(formState.translationsDescription)) {
            return false;
        }
        return defaultLanguage in formState.translationsDescription && Object.keys(formState.translationsDescription).length > 1;
    }, [formState, defaultLanguage, event]);

    const taxToUse = React.useMemo(() => {
        if (!taxes || (!formState.tax_id && (!event || !event.tax_id))) return null;
        let foundTax = taxes.find(t => t.id === formState.tax_id)?.tax_values;
        //we try using the event tax if no tax selected
        if (!foundTax) {
            foundTax = taxes.find(t => t.id === event.tax_id)?.tax_values;
        }
        return foundTax.tax_value + foundTax.tax_supplemental_value;
    }, [taxes, event?.tax_id, formState.tax_id]);

    const includeTaxInPrice = React.useCallback(
        (withTaxes, value) => {
            const finalPrice = withTaxes ? value * (1 + taxToUse / 100) : value / (1 + taxToUse / 100);
            //cap to two digits so the division doesn't fill the field
            return +finalPrice.toFixed(2);
        },
        [taxToUse]
    );

    const handleSubmit = React.useCallback(() => {
        // validate data
        if (!formValidate()) {
            return toast.error(t('ticket.common.errorFields'));
        }
        formState.fees.forEach(fee => {
            if (!fee.name) {
                fee.name = t('ticket.rates.infos.fees.service_fee');
            }
        });

        let _fees = formState.fees.filter(fee => undefined_or_null_or_value(fee.amount) !== null);
        // If we only update the VAT, without changing the value of one or more charges,
        // we need to update the amount excluding VAT of each fee.
        _fees = _fees.map(fee => ({ ...fee, amount_no_tax: String(includeTaxInPrice(false, fee.amount)) }));

        let rateTmp = {
            uuid: rateId,
            id: rateId,
            inventories: formState.inventories ? new Array(formState.inventories) : [],
            name: formState.name,
            price: formState.price,
            contingents: formState.contingents || [],
            category: formState.category,
            group: formState.group,
            form_id: formState.form_id,
            event_ids: selectedEvents,
            rate_template_id: formState.rate_template_id,
            type: formState.type,
            quantity: formState.quantity,
            tax_id: formState.tax_id,
            disabled: formState.disabled,
            description: formState.description,
            fees: _fees,
            cart_max_quantity: formState.maxQuantity,
            cart_min_quantity: formState.minQuantity,
            bundle_size: formState.bundleSize,
            preselected_quantity: formState.defaultQuantity,
            visibility_start_date: formState.visibilityStartDate,
            visibility_end_date: formState.visibilityEndDate,
            relays: formState.relays,
            is_passculture: formState.is_passculture
        };

        if (event) {
            // handle single language case
            const nameTranslation = {};
            const descriptionTranslation = {};

            if (rateId) {
                for (let key of Object.keys(formState.translationsName)) {
                    nameTranslation[key] = null;
                }

                for (let key of Object.keys(formState.translationsDescription)) {
                    descriptionTranslation[key] = null;
                }
            }

            rateTmp.translations = {
                name: nameTranslation,
                description: descriptionTranslation
            };
        }

        if (hasNameTranslations) {
            rateTmp.translations['name'] = formState.translationsName;
        }

        if (hasDescriptionTranslations) {
            rateTmp.translations['description'] = formState.translationsDescription;
        }

        if (formerGroupId !== rateTmp.group || !rateId) {
            rateTmp.display_order = handleDisplayOrder(rateTmp);
        }

        const rateObject = new RateClass(rateTmp);
        setLoading(true);
        rateObject
            .save({
                organizationId,
                eventId: event ? event.id : null
            })
            .then(() => {
                const trackingData = {
                    'Page Title': 'RATE',
                    'Activate Pass Culture': rateObject.is_passculture
                };
                mixpanel.track('ACTION > VALIDATE RATE', trackingData);
                toast.success(t(`ticket.rates.${rateId ? 'editSuccess' : 'creationSuccess'}`));
                history.push(prefixUrl());
            })
            .catch(e => {
                toast.error(t(`ticket.rates.${rateId ? 'editError' : 'creationError'}`));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [organizationId, formState, mixpanel, event, formValidate, selectedEvents, handleDisplayOrder, hasNameTranslations, hasDescriptionTranslations, rate, includeTaxInPrice]);

    const labelBodyConfirmModal = React.useMemo(() => {
        //Case create or update with selectedEvents
        if (selectedEvents.length) {
            return t('ticket.rates.confirmModal.labelBodyWithSelectedEvents', { name: formState.name, count: selectedEvents.length });
        }

        if (event && !formState?.rate_template_id) {
            return t('ticket.rates.confirmModal.labelBodyUnlinkTemplate');
        }

        //Case create without selectedEvents
        if (!rateId) {
            return t('ticket.rates.confirmModal.labelBodyWithoutSelectedEvents');
        }

        return '';
    }, [selectedEvents, rateId, formState, event]);

    const onChangeEvents = React.useCallback(selected => {
        setSelectedEvents(selected.map(event => event.id));
    }, []);

    const handleRateLinker = React.useCallback(
        rate_template_id => {
            if (!rate_template_id && !formState?.rate_template_id) {
                return null;
            }
            //Null ? copy the data and link it
            let tmpForm = { ...formState };
            //Case 1: From rate_template to null, we copy the value
            if (!rate_template_id && formState?.rate_template_id) {
                let rate_template = rateTemplates.find(r => r.pk === formState.rate_template_id);
                if (!rate_template) {
                    return;
                }
                for (let key of Object.keys(formState)) {
                    tmpForm[key] = formState[key] ?? rate_template[key] ?? null;
                }
            }
            // //Case 2: We add a rate_template_id and we didn't have one before
            let new_rate_template = rateTemplates.find(r => r.pk === rate_template_id);
            if (rate_template_id && !formState?.rate_template_id) {
                for (let key of Object.keys(new_rate_template.object)) {
                    if (formState[key] === new_rate_template[key] && Object.prototype.hasOwnProperty.call(formState, key)) {
                        tmpForm[key] = new_rate_template[key];
                    }
                }
            }

            tmpForm.rate_template_id = rate_template_id;

            const selectedGroupId = formState.group || groups.find(group => group.group_template_id === new_rate_template?.group)?.id || null;
            onChangeFormObject({
                ...tmpForm,
                group: selectedGroupId
            });

            if (!tmpForm?.rate_template_id) {
                rate.rate_template = null;
            } else {
                rate.rate_template = rateTemplates.find(r => r.pk === tmpForm.rate_template_id);
            }
            let newRate = new RateClass(rate);
            setRate(newRate);
        },
        [rate, formState, rateTemplates, groups]
    );

    const displayModalOrSubmit = React.useCallback(() => {
        if (!event || (hasRateTemplateId && !rate?.rate_template)) {
            return setShowConfirmModal(true);
        }
        return handleSubmit();
    }, [event, hasRateTemplateId, rate, handleSubmit]);

    const rateLimit = React.useMemo(() => {
        if (!event) {
            return false;
        }
        return formState.price > formState.maxPrice;
    }, [formState, event]);

    const rateTooLow = React.useMemo(() => {
        return formState.type !== 'free' && formState.price < 1 && !formState.rate_template_id;
    }, [formState]);

    const isAllowedTicketManagement = React.useMemo(() => {
        return permissions.hasCapacities(['PyvarCapacity.RATE_EXCHANGE_ADMIN', 'PyvarCapacity.RATE_EXCHANGE'], organization.id) && RateClass !== RateTemplate && !rate?.distributor;
    }, [permissions, organization, rate, RateClass, RateTemplate]);

    const rateNameTooLong = React.useMemo(() => {
        return formState.is_passculture && formState.name.length > 50;
    }, [formState]);

    const isDuplicatePassCultureName = React.useMemo(() => {
        if (!formState.is_passculture) return false;

        const currentName = formState.name?.trim().toLowerCase();
        return rates.some(rate => rate.is_passculture && String(rate.id) !== rateId && rate.name?.trim().toLowerCase() === currentName);
    }, [rates, formState, rateId]);

    if (!isLoading && (pageErrorCode || !rate)) {
        let backHomeUrl = event ? `/ticket/O${organizationId}/E${event.id}/settings/rates` : `/ticket/O${organizationId}/events`;
        return <PageError errorCode={pageErrorCode || 500} backHomeUrl={backHomeUrl} />;
    }

    if (isLoading || (event && loadingQuotas)) {
        return <Loading />;
    }

    function HelpTitle() {
        return (
            <>
                {t('ticket.rates.passculture.title')}
                <TooltipHelp title={t('ticket.rates.passculture.tooltip')} mini position={'top'} />
            </>
        );
    }

    return (
        <div className={Styles['rate-creation-container']}>
            {showConfirmModal && (
                <ConfirmModal
                    labelTitle={t(`ticket.rates.modal-confirm-${RateClass === RateTemplate ? 'link' : 'unlink'}.title`)}
                    objectToSave={formState}
                    onClose={() => setShowConfirmModal(false)}
                    onAccept={handleSubmit}
                    labelBody={labelBodyConfirmModal}
                />
            )}
            <WeezHeader
                title={
                    <div className={Styles['rate-title-container']}>
                        {rateId ? (
                            <>
                                <span>{rate?.name || rate?.rate_template?.name}</span>
                                <RateMembershipInfo rate={rate} detail={true} />
                                <span className={Styles['rate-distributor']}>
                                    {rate?.distributor ? (
                                        <span>({rate?.distributor?.label})</span>
                                    ) : (
                                        // In a rate template context, there is no event or quotas to display
                                        event && <RateStatus quotas={quotas} rate={rate} ratesList={rates} settings={settings} event={event} />
                                    )}
                                </span>
                            </>
                        ) : (
                            t('ticket.rates.newRate')
                        )}
                    </div>
                }
                backLink={{
                    onClick: () => {
                        history.push(prefixUrl());
                        track('ACTION > BACKHEADER', { 'Page Title': 'NEW RATE' });
                    }
                }}
                rightComponent={[
                    <Button
                        primary
                        key={'submit'}
                        onClick={() => {
                            displayModalOrSubmit();
                        }}
                        label={t(`ticket.common.action.save`)}
                        disabled={Object.entries(formErrors).length > 0 || rateLimit || rateTooLow}
                    />
                ]}
            />
            {isPassCultureEventActivated && (rateNameTooLong || isDuplicatePassCultureName) && (
                <Message type="warning" title={t('ticket.common.warning')}>
                    {rateNameTooLong && <div>• {t('ticket.rates.infos.warning-message.name-too-long')}</div>}
                    {isDuplicatePassCultureName && <div>• {t('ticket.rates.infos.warning-message.duplicated-rate-name')}</div>}
                </Message>
            )}
            <Card.FormManager useSteps={false}>
                <Card.FormStep
                    title={t(`ticket.rates.infos.title`)}
                    subtitle={t('ticket.rates.infos.subtitle')}
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            {t('ticket.rates.infos.tips')}
                        </Message>
                    }
                >
                    <>
                        <RateFormInfos
                            organizationId={organizationId}
                            event={event}
                            organization={organization}
                            defaultLanguage={defaultLanguage}
                            languageOptions={languageOptions}
                            rate={rate}
                            rateLimit={rateLimit}
                            form={formState}
                            formErrors={formErrors}
                            rateTemplates={rateTemplates}
                            onChangeForm={onChangeForm}
                            groups={groups}
                            forms={forms}
                            handleRateLinker={handleRateLinker}
                            rateTypes={RATE_TYPES}
                            onChangeFormObject={onChangeFormObject}
                            taxes={taxes}
                            includeTaxInPrice={includeTaxInPrice}
                            taxToUse={taxToUse}
                        />
                        {rate && !rate?.distributor && isPassCultureEventActivated && (
                            <ToggleCard
                                title={<HelpTitle />}
                                subtitle={t('ticket.rates.passculture.subtitle')}
                                logoURL={PassCultureLogo}
                                key={'passculture'}
                                toggle={formState.is_passculture}
                                onToggle={value => onChangeForm('is_passculture', value)}
                            />
                        )}
                    </>
                </Card.FormStep>
                {((rate && !rate?.distributor && charts?.length > 0 && !event) ||
                    event?.is_seated ||
                    (event?.isFakeSeating() && (inventories?.length > 1 || categories?.length > 1))) && (
                    <Card.FormStep
                        title={t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.title' : 'ticket.rates.seating.title')}
                        subtitle={t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.subtitle' : 'ticket.rates.seating.subtitle')}
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.rates.seating.tips')}
                            </Message>
                        }
                    >
                        <RateFormSeating
                            organizationId={organizationId}
                            event={event}
                            rate={rate}
                            form={formState}
                            formErrors={formErrors}
                            rateTemplates={rateTemplates}
                            onChangeForm={onChangeForm}
                            categories={categories}
                            inventories={inventories}
                            contingents={contingents}
                            charts={charts}
                            onChangeFormObject={onChangeFormObject}
                        />
                    </Card.FormStep>
                )}
                {RateClass === RateTemplate && !rate?.distributor && (
                    <Card.FormStep
                        title={t(`ticket.rates.settingEvents.title`)}
                        subtitle={t('ticket.rates.settingEvents.subtitle')}
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.rates.settingEvents.tips')}
                            </Message>
                        }
                    >
                        <RateFormSettingsEvents
                            organizationId={organizationId}
                            event={event}
                            rate={rate}
                            form={formState}
                            formErrors={formErrors}
                            onChangeForm={onChangeForm}
                            onChangeFormObject={onChangeFormObject}
                            onChangeEvents={onChangeEvents}
                            selectedEvents={selectedEvents}
                            events={events}
                        />
                    </Card.FormStep>
                )}
            </Card.FormManager>

            {RateClass !== RateTemplate && !rate?.distributor && (
                <OptionalForm
                    labelOpen={<span>{t(`ticket.rates.optionalSettings.titleClose`)}</span>}
                    labelClosed={<span>{t(`ticket.rates.optionalSettings.titleOpen`)}</span>}
                    displayAdditional
                    open={openOptionalForm}
                    onToggle={toggle => {
                        setOpenOptionalForm();
                        track('ACTION > DISPLAY ADVANCED OPTIONS', { 'Page Title': 'NEW RATE', Open: toggle });
                    }}
                >
                    <ReleaseCard
                        form={formState}
                        onChange={(key, value) => {
                            onChangeForm(key, value);
                            formValidate();
                        }}
                        onSetForm={form => {
                            onChangeFormObject(form, true);
                        }}
                        errors={formErrors}
                        event={event}
                        rates={rates}
                        rate={rate}
                        salesStart={salesStart}
                        onChangeSalesStart={setSalesStart}
                    />
                    <CartCard
                        form={formState}
                        onChange={(key, value) => {
                            onChangeForm(key, value);
                        }}
                        onSetForm={form => {
                            onChangeFormObject(form, true);
                        }}
                        rate={rate}
                        event={event}
                        validate={formValidate}
                        errors={formErrors}
                    />
                    {rateId && <TicketManagement organization={organization} event={event} rate={rate} isAllowedTicketManagement={isAllowedTicketManagement} />}
                </OptionalForm>
            )}
        </div>
    );
}

RateForm.propDefaults = {
    rateId: null,
    event: null,
    menhirUrl: null
};

RateForm.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object,
    rateId: PropTypes.string,
    menhirUrl: PropTypes.string,
    history: PropTypes.object.isRequired,
    RateClass: PropTypes.func.isRequired,
    GroupClass: PropTypes.func.isRequired
};
